import apiClient from './apiClient';

// ============================================================
// Types — matching backend Go models for J202 (樣品繳庫單)
// ============================================================

export interface FJ120 {
  fj120_01: string;          // 繳庫單號 (PK)
  fj120_10: string;          // 樣品申請單號(來源)
  fj120_11: string | null;   // 繳庫日期 (ISO string)
  fj120_12: string;          // 繳庫庫位
  fj120_13: boolean;         // 含稅旗標
  fj120_14: string;          // 廠別代碼
  fj120_15: number;          // 未稅金額
  fj120_151: number;         // 含稅金額
  fj120_16: number;          // 稅額
  fj120_17: string;          // 預留
  fj120_18: string;          // 輸單人員
  fj120_19: string | null;   // 預留日期
  fj120_20: string;          // 預留
  fj120_21: string | null;   // 預留日期2
  fj120_22: number;          // 總項數
  fj120_23: string;          // 廠商代號
  fj120_30: string;          // 備註代號
  fj120_31: string;          // 備註說明
  fj120_80: string;          // 核准狀態 N=草稿/已沖銷 D=送審中 P=已核准（自創碼 S/R/V 已廢除）
  fj120_89: string;          // 預留
  fj120_90: number;          // version
  fj120_91: string;          // 異動人員
  fj120_92: string | null;   // 異動日期
  fj120_93: string;          // 建立人員
  details?: FJ121[];
}

export interface FJ121 {
  fj121_01: string;          // 繳庫單號 FK
  fj121_02: number;          // 項次
  fj121_10: string;          // 產品代號1
  fj121_11: string;          // 產品代號2
  fj121_12: string;          // 色號
  fj121_121: string;         // 中文色名
  fj121_122: string;         // 英文色名
  fj121_12a: string;         // CBNO
  fj121_13: number;          // 數量
  fj121_14: string;          // 單位
  fj121_15: number;          // 未稅單價
  fj121_151: number;         // 含稅單價
  fj121_16: number;          // 未稅金額
  fj121_161: number;         // 含稅金額
  fj121_17: string;          // 等級代號
  fj121_171: string;         // 等級說明
  fj121_18: number;          // 單位換算率
  fj121_19: string;          // 庫存單位
  fj121_20: string;          // 產品名稱
  fj121_21: string;          // 產品規格
  fj121_22: string;          // 加工別
  fj121_23: number;          // 幅寬1
  fj121_24: number;          // 幅寬2
  fj121_25: number;          // 重量1
  fj121_26: number;          // 重量2
  fj121_27: string;          // 印花配色
  fj121_31: string;          // 預留欄位1
  fj121_32: string;          // 預留欄位2
  fj121_35: string;          // 預留欄位5
  fj121_90: number;          // version
}

// ============================================================
// Request / Response types
// ============================================================

export interface J202ListResponse {
  data: FJ120[];
  total: number;
  page: number;
  size: number;
}

export interface J202CreateRequest {
  master: Partial<FJ120>;
  details: Partial<FJ121>[];
}

export interface ValidationError {
  field: string;
  rule: string;
  message: string;
}

// ============================================================
// Approval types
// ============================================================

export interface ApprovalResponse {
  request_no: string;
  action: string;       // SUBMIT | APPROVE | REJECT
  old_status: string;
  new_status: string;
  message: string;
}

export interface ApprovalHistory {
  id: number;
  request_no: string;
  action: string;
  actor_id: string;
  actor_name: string;
  comment: string;
  old_status: string;
  new_status: string;
  created_at: string;
}

// ============================================================
// API methods
// ============================================================

export const j202Api = {
  async list(page = 1, size = 20): Promise<J202ListResponse> {
    const res = await apiClient.get('/api/j202', { params: { page, size } });
    return res.data;
  },

  async get(id: string): Promise<FJ120> {
    const res = await apiClient.get(`/api/j202/${encodeURIComponent(id)}`);
    return res.data;
  },

  async create(data: J202CreateRequest): Promise<FJ120> {
    const res = await apiClient.post('/api/j202', data);
    return res.data;
  },

  async update(id: string, data: J202CreateRequest): Promise<FJ120> {
    const res = await apiClient.put(`/api/j202/${encodeURIComponent(id)}`, data);
    return res.data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/j202/${encodeURIComponent(id)}`);
  },

  // ============================================================
  // Approval workflow
  // ============================================================

  async submitForApproval(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/j202/${encodeURIComponent(id)}/submit`, { comment: comment || '' });
    return res.data;
  },

  async approve(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/j202/${encodeURIComponent(id)}/approve`, { comment: comment || '' });
    return res.data;
  },

  async reject(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/j202/${encodeURIComponent(id)}/reject`, { comment: comment || '' });
    return res.data;
  },

  async writeoff(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/j202/${encodeURIComponent(id)}/writeoff`, { comment: comment || '' });
    return res.data;
  },

  async getApprovalHistory(id: string): Promise<ApprovalHistory[]> {
    const res = await apiClient.get(`/api/j202/${encodeURIComponent(id)}/approval-history`);
    return res.data.data || [];
  },
};
